<template>
    <BaseDialog :open="open" :title="`Gruppenarbeit: ${config.name}`" size="xl" @close="closeGroupBuilder">
        <div class="builder">
            <div class="options">
                <div class="field">
                    <span class="field-label" id="team-mode">Teams bilden nach</span>
                    <div class="mode-row">
                        <div class="segmented" role="group" aria-labelledby="team-mode">
                            <button type="button" :aria-pressed="mode === 'size' ? 'true' : 'false'" @click="mode = 'size'">Teamgrösse</button>
                            <button type="button" :aria-pressed="mode === 'count' ? 'true' : 'false'" @click="mode = 'count'">Anzahl Teams</button>
                        </div>
                        <div class="stepper" role="group" :aria-label="mode === 'size' ? 'Teamgrösse' : 'Anzahl Teams'">
                            <button type="button" aria-label="Eins weniger" :disabled="value <= 1" @click="value -= 1"><Minus /></button>
                            <output>{{ value }}</output>
                            <button type="button" aria-label="Eins mehr" :disabled="value >= maxValue" @click="value += 1"><Plus /></button>
                        </div>
                    </div>
                    <span class="field-hint">{{ preview }}</span>
                </div>

                <div class="field">
                    <span class="field-label">Wer fehlt heute?</span>
                    <div class="student-list">
                        <button type="button" class="pill student" v-for="student in config.students" :key="student.id"
                            :class="{ hatched: isAbsent(student.id) }" :aria-pressed="isAbsent(student.id) ? 'true' : 'false'"
                            @click="toggleStudent(student.id)">
                            <span>{{ student.name }}</span>
                            <span v-if="isAbsent(student.id)" class="visually-hidden">fehlt</span>
                        </button>
                    </div>
                    <span class="field-hint">Klicke Abwesende an: schraffiert heisst, die Person fehlt und kommt in kein Team.</span>
                </div>

                <div class="actions">
                    <button type="button" class="btn btn-primary" :disabled="presentStudents.length === 0" @click="buildGroups">
                        <Dices aria-hidden="true" /> {{ groups.length ? 'Neu mischen' : 'Teams erstellen' }}
                    </button>
                    <button type="button" class="btn" v-if="groups.length" @click="copyGroups">
                        <component :is="copied ? 'Check' : 'Copy'" aria-hidden="true" /> {{ copied ? 'Kopiert' : 'Kopieren' }}
                    </button>
                </div>
                <p v-if="conflicts > 0" class="form-note" role="status">
                    Nicht alle Trenngruppen liessen sich aufteilen: {{ conflicts === 1 ? 'einmal sind' : `${conflicts}-mal sind` }} zwei aus derselben Gruppe im gleichen Team. Mit mehr Teams geht es auf.
                </p>
                <p v-if="split > 0" class="form-note" role="status">
                    {{ split === 1 ? 'Eine Partnergruppe ist grösser als ein Team und wurde' : `${split} Partnergruppen sind grösser als ein Team und wurden` }} aufgeteilt. Mit grösseren Teams bleiben sie zusammen.
                </p>
            </div>

            <div class="groups" aria-live="polite">
                <h3 class="leaf-title">Teams</h3>
                <p class="hint" v-if="groups.length == 0">Noch keine Teams. Wähle die Grösse und klicke auf «Teams erstellen».</p>
                <div class="group-container">
                    <div class="group" v-for="(group, index) in groups" :key="index">
                        <h4 class="group-title">Team {{ index + 1 }}</h4>
                        <ul class="group-members">
                            <li v-for="student in group" :key="student.id">{{ student.name }}</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </BaseDialog>
</template>

<script>
import { Check, Copy, Dices, Minus, Plus } from '@lucide/vue';
import BaseDialog from './ui/BaseDialog.vue';
import { buildTeams, teamCount } from '../seating.js';

export default {
    name: "GroupBuilder",
    data() {
        return {
            mode: 'size',
            value: 2,
            absentPeople: [],
            groups: [],
            conflicts: 0,
            // Partner groups that didn't fit into one team
            split: 0,
            copied: false,
        }
    },
    components: {
        BaseDialog, Check, Copy, Dices, Minus, Plus,
    },
    props: {
        config: Object,
        open: Boolean,
    },
    emits: ['closeGroupBuilder'],
    watch:{
        'config.id': function() {
            this.groups = []
            this.conflicts = 0
            this.split = 0
            this.absentPeople = []
        },
        mode: function() {
            this.value = Math.min(this.value, this.maxValue)
        }
    },
    computed: {
        presentStudents() {
            return this.config.students.filter(s => !this.isAbsent(s.id))
        },
        maxValue() {
            return Math.max(1, Math.min(this.presentStudents.length, 20))
        },
        // What the current setting gives, before anything is shuffled
        preview() {
            const present = this.presentStudents.length
            if (present === 0) return 'Niemand ist da.'
            const count = teamCount(present, this.mode, this.value)
            const small = Math.floor(present / count)
            const sizes = present % count === 0 ? `je ${small}` : `mit ${small} oder ${small + 1}`
            return `${present} Anwesende: ${count === 1 ? '1 Team' : `${count} Teams`} ${sizes} Personen.`
        }
    },
    methods: {
        closeGroupBuilder: function() {
            this.$emit("closeGroupBuilder")
        },
        buildGroups: function() {
            const result = buildTeams(this.presentStudents, this.mode, this.value)
            this.groups = result.teams
            this.conflicts = result.conflicts
            this.split = result.split
            this.copied = false
        },
        copyGroups: function() {
            const text = this.groups.map((group, index) => `Team ${index + 1}: ${group.map(s => s.name).join(', ')}`).join('\n')
            navigator.clipboard.writeText(text).then(() => { this.copied = true })
        },
        toggleStudent: function(id) {
            if (this.isAbsent(id)) {
                this.absentPeople = this.absentPeople.filter(p => p != id)
            } else {
                this.absentPeople.push(id)
            }
        },
        isAbsent: function(id) {
            return this.absentPeople.includes(id)
        }
    }
}

</script>

<style scoped>
.builder {
    display: grid;
    grid-template-columns: minmax(280px, 2fr) minmax(0, 3fr);
    gap: var(--s-5);
    align-items: start;
}

.options {
    display: grid;
    gap: var(--s-4);
}

.mode-row {
    display: flex;
    flex-wrap: wrap;
    gap: var(--s-2);
}

.mode-row .stepper output {
    min-width: 48px;
}

.student-list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--s-2);
    padding: var(--s-1) 0;
}

.student {
    cursor: pointer;
    transition: background-color var(--dur-fast) var(--ease);
}

.student:not(.hatched):hover {
    background: var(--hue-wash, var(--ground-wash));
}

.groups {
    display: grid;
    gap: var(--s-3);
    padding: var(--s-4);
    border: var(--line);
    border-radius: var(--r-control);
    background: var(--hue-wash, var(--ground-wash));
    min-height: 240px;
    align-content: start;
}

.group-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: var(--s-3);
}

.group {
    padding: var(--s-3);
    border: var(--line);
    border-radius: var(--r-control);
    background: var(--paper);
    box-shadow: var(--shadow-sm);
}

.group-title {
    padding-bottom: var(--s-1);
    margin-bottom: var(--s-2);
    border-bottom: var(--line);
}

.group-members {
    margin: 0;
    padding: 0;
    list-style: none;
    font-weight: 700;
    line-height: 1.35;
}

.group-members li {
    padding: 2px 0;
    overflow-wrap: anywhere;
}

@media (max-width: 899px) {
    .builder {
        grid-template-columns: minmax(0, 1fr);
    }
}
</style>
